/** Длина строки в байтах UTF-8 — лимиты сервера считаются в байтах, а не в символах. */
export const utf8Bytes = (s: string): number => {
    let n = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 0x80) n += 1;
        else if (c < 0x800) n += 2;
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            const d = s.charCodeAt(i + 1);
            if (d >= 0xdc00 && d <= 0xdfff) {
                n += 4;
                i++;
            } else n += 3;
        } else n += 3;
    }
    return n;
};

const EMAIL_RE = /[^\s@/?#&=:;,<>()"'[\]]+(?:@|%40)[^\s@/?#&=:;,<>()"'[\]]+\.[A-Za-z]{2,}/g;
const DIGITS_RE = /\d{6,}/g;

export const redactEmails = (s: string): string => s.replace(EMAIL_RE, "[email]");

const cut = (s: string, max: number): string =>
    s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s;

/** Идентификаторы и метки: только без почты — хеши и id с цифрами нужны целиком. */
export const cleanValue = (s: unknown, max: number): string =>
    s === null || s === undefined ? "" : cut(redactEmails(String(s).trim()), max);

/**
 * Свободный текст (надписи, заголовки, запросы): пробелы схлопнуты, адреса почты
 * и длинные числа (телефоны, номера карт и заказов) заменены.
 */
export const cleanText = (s: unknown, max: number): string => {
    if (s === null || s === undefined) return "";
    let t = String(s).replace(/\s+/g, " ").trim();
    if (t.length > max * 4) t = t.slice(0, max * 4);
    return cut(redactEmails(t).replace(DIGITS_RE, "[num]"), max);
};

// Ссылка на подарок /<lang>/gift/<token>: токен и есть подарок (кто знает — тот
// получит). index.html убирает его из адреса ещё до журнала, а это — страховка:
// переход внутри сайта, старый кэш страницы, from= со ссылкой.
const GIFT_TOKEN_IN_PATH_RE = /\/gift\/[A-Za-z0-9_-]{8,}/g;
export const redactGiftTokens = (s: string): string => s.replace(GIFT_TOKEN_IN_PATH_RE, "/gift/[gift]");

// Путь чистим только от почты (и токена подарка): числа в пути — это id лекций
// и клипов, без них запись бесполезна.
export const sanitizePath = (p: string): string => cut(redactGiftTokens(redactEmails(p || "")), 300);

const VALUE_KEYS = new Set([
    "q",
    "t",
    "m",
    "from",
    "hash",
    "tab",
    "source",
    "ref",
    "payment_success",
    "tokens_paid",
]);
const keepsValue = (k: string): boolean => VALUE_KEYS.has(k) || k.startsWith("utm_");

/**
 * Ключи строки запроса — всегда, значения — только у безопасных ключей.
 * `null` значит «параметр был, значение не записываем» (session_id Stripe,
 * токены из писем, redeem и прочее).
 */
export const sanitizeQuery = (
    search: string,
): Record<string, string | null> | undefined => {
    let sp: URLSearchParams;
    try {
        sp = new URLSearchParams(search || "");
    } catch {
        return undefined;
    }
    const out: Record<string, string | null> = {};
    let n = 0;
    sp.forEach((value, rawKey) => {
        if (n >= 20) return;
        const key = cut(redactEmails(rawKey), 40);
        if (key in out) return;
        n++;
        if (!keepsValue(key)) out[key] = null;
        else if (key === "q") out[key] = cleanText(value, 150);
        else out[key] = cut(redactGiftTokens(redactEmails(value)), 150);
    });
    return n > 0 ? out : undefined;
};

const FRAGMENT_RE = /^#[\w-]{1,40}$/;

/**
 * Ссылка для журнала: свой сайт — путь и безопасные параметры, чужой — только
 * origin и путь. Фрагмент остаётся лишь якорем: после входа через Google в нём
 * лежит #id_token=…
 */
export const sanitizeUrl = (href: string, base?: string): string => {
    let u: URL;
    try {
        u = new URL(href, base || window.location.href);
    } catch {
        return "";
    }
    if (u.protocol !== "http:" && u.protocol !== "https:") return u.protocol;
    const path = sanitizePath(u.pathname);
    if (u.origin !== window.location.origin) return cut(`${u.origin}${path}`, 300);
    const q = sanitizeQuery(u.search);
    const qs = q
        ? `?${Object.entries(q)
              .map(([k, v]) => (v === null ? k : `${k}=${v}`))
              .join("&")}`
        : "";
    const frag = FRAGMENT_RE.test(u.hash) ? u.hash : "";
    return cut(`${path}${qs}${frag}`, 300);
};

const trimDeep = (v: unknown, strMax: number, arrMax: number, depth: number): unknown => {
    if (typeof v === "string") return cut(v, strMax);
    if (v === null || typeof v !== "object") return v;
    if (depth > 6) return "[deep]";
    if (Array.isArray(v)) {
        return v.slice(0, arrMax).map((x) => trimDeep(x, strMax, arrMax, depth + 1));
    }
    const out: Record<string, unknown> = {};
    let n = 0;
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
        if (n++ >= 50) break;
        out[k] = trimDeep(x, strMax, arrMax, depth + 1);
    }
    return out;
};

type Clampable = { data?: Record<string, unknown>; ctx?: Record<string, unknown> };

const measure = (ev: unknown): number => {
    try {
        return utf8Bytes(JSON.stringify(ev));
    } catch {
        return Infinity;
    }
};

/**
 * Держит событие в пределах лимита сервера (4 КБ UTF‑8, с запасом на `rt`).
 * Сначала режет строки и массивы, в крайнем случае оставляет от data только
 * список ключей — лучше знать, что событие было, чем потерять его целиком.
 */
export const clampEvent = <T extends Clampable>(
    ev: T,
    maxBytes: number,
): { ev: T; bytes: number } => {
    let bytes = measure(ev);
    if (bytes <= maxBytes) return { ev, bytes };
    const steps: Array<[number, number]> = [
        [300, 20],
        [120, 20],
        [60, 10],
    ];
    for (const [strMax, arrMax] of steps) {
        const next: T = {
            ...ev,
            data: ev.data && (trimDeep(ev.data, strMax, arrMax, 0) as T["data"]),
            ctx: ev.ctx && (trimDeep(ev.ctx, strMax, arrMax, 0) as T["ctx"]),
        };
        bytes = measure(next);
        if (bytes <= maxBytes) return { ev: next, bytes };
    }
    const last: T = {
        ...ev,
        data: ev.data
            ? { truncated: true, keys: Object.keys(ev.data).slice(0, 20).map((k) => cut(k, 40)) }
            : undefined,
        ctx: ev.ctx ? { truncated: true } : undefined,
    };
    return { ev: last, bytes: measure(last) };
};
